import React, { useState, useEffect } from 'react';
import { API_URLS } from '../config';
import './styles/AccountSettings.css';

const AccountSettings = ({ name: initialName, email: initialEmail, setName: setGlobalName, setEmail: setGlobalEmail }) => {
  const token = localStorage.getItem('token');
  const [localName, setLocalName] = useState(initialName || '');
  const [localEmail, setLocalEmail] = useState(initialEmail || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track the original values to compare on submit
  const [originalName, setOriginalName] = useState(initialName || '');
  const [originalEmail, setOriginalEmail] = useState(initialEmail || '');

  // Update local and original values if props change (e.g., after a successful update)
  useEffect(() => {
    setLocalName(initialName || '');
    setOriginalName(initialName || '');
  }, [initialName]);

  useEffect(() => {
    setLocalEmail(initialEmail || '');
    setOriginalEmail(initialEmail || '');
  }, [initialEmail]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    let madeProfileUpdateAttempt = false;
    let madePasswordUpdateAttempt = false;
    const errorMessages = [];
    const successMessages = [];

    // Compare against original values, not just initial props
    const didNameChange = localName !== originalName;
    const didEmailChange = localEmail !== originalEmail;
    console.log('Name changed:', didNameChange, 'Email changed:', didEmailChange);
  

    // Attempt to update profile information (name/email) if changed
    if (didNameChange || didEmailChange) {
      madeProfileUpdateAttempt = true;
      const profilePayload = { name: localName, email: localEmail };
      try {
        const response = await fetch(API_URLS.updateAccount, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profilePayload),
        });
        const data = await response.json();
        if (response.ok) {
          // profileUpdateSuccess = true;
          successMessages.push('Profile information updated successfully!');
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (didNameChange) {
            storedUser.name = localName;
            setGlobalName(localName);
          }
          if (didEmailChange) {
            storedUser.email = localEmail;
            setGlobalEmail(localEmail);
          }
          localStorage.setItem('user', JSON.stringify(storedUser));
          setGlobalEmail(localEmail);
          setGlobalName(localName);
        } else {
          errorMessages.push(data.message || 'Failed to update profile information.');
        }
      } catch (error) {
        console.error('Error updating profile information:', error);
        errorMessages.push('An error occurred while updating profile information.');
      }
    }

    // Attempt to change password if newPassword is provided
    if (newPassword) {
      madePasswordUpdateAttempt = true;
      if (!currentPassword) {
        errorMessages.push('Current password is required to change your password.');
      } else if (newPassword !== confirmNewPassword) {
        errorMessages.push('New passwords do not match.');
      } else {
        const passwordPayload = { currentPassword, newPassword };
        try {
          // Assuming API_URLS.updatePassword exists or will be added to your config.js
          const response = await fetch(API_URLS.updatePassword, { 
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(passwordPayload),
          });
          const data = await response.json();
          if (response.ok) {
            // passwordUpdateSuccess = true;
            successMessages.push('Password changed successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
          } else {
            errorMessages.push(data.message || 'Failed to change password.');
          }
        } catch (error) {
          console.error('Error changing password:', error);
          errorMessages.push('An error occurred while changing password.');
        }
      }
    }

    setIsSubmitting(false);

    if (errorMessages.length > 0) {
      setError(errorMessages.join(' '));
    }
    if (successMessages.length > 0) {
      setSuccessMessage(successMessages.join(' '));
    }
    
    // Handle case where save is clicked with no actual changes intended by the user in the form
    if (!madeProfileUpdateAttempt && !madePasswordUpdateAttempt && errorMessages.length === 0) {
        // If user clicked save without changing anything and no other errors (like password mismatch if attempted)
        setSuccessMessage('No changes were submitted.'); 
    }
  };

  return (
    <form className="account-form" onSubmit={handleSubmit}>
      <h3>Account Settings</h3>
      
      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
      
      <div className="form-section">
        <h4 className="section-title">Profile Information</h4>
        <div className="account-group">
          <label htmlFor="name">Name:</label>
          <input
            id="name"
            className="form-control"
            type="text"
            value={localName}
            onChange={(e) => { setLocalName(e.target.value);}}
            required
          />
        </div>
        <div className="account-group">
          <label htmlFor="email">Email:</label>
          <input
            id="email"
            className="form-control"
            type="email"
            value={localEmail}
            onChange={(e) => { setLocalEmail(e.target.value);}}
            required
          />
        </div>
      </div>
      
      <div className="form-section">
        <h4 className="section-title">Change Password (optional)</h4>
        <div className="password-group">
          <div className="account-group">
            <label htmlFor="currentPassword">Current Password:</label>
            <input
              id="currentPassword"
              className="form-control"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required={!!newPassword}
            />
          </div>
          <div className="account-group">
            <label htmlFor="newPassword">New Password:</label>
            <input
              id="newPassword"
              className="form-control"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="account-group">
            <label htmlFor="confirmNewPassword">Confirm New Password:</label>
            <input
              id="confirmNewPassword"
              className="form-control"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required={!!newPassword}
            />
          </div>
        </div>
      </div>
      
      <button 
        className="submit-button" 
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

export default AccountSettings;